import {
  ChangeEvent,
  ReactNode,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { siteTexts } from "../i18n/siteTexts";
import "./ImageUploadField.css";
import Button from "./Button";
import useBodyScrollLock from "../hooks/useBodyScrollLock";

type ImageUploadFieldProps = {
  label: string;
  value: string;
  onChange: (nextValue: string) => void | Promise<void>;
  onRemove?: () => void | Promise<void>;
  aspectRatio?: number;
  previewSquare?: boolean;
  previewCircle?: boolean;
  outputWidth?: number;
  maxFileBytes?: number;
  applyCropLabel?: string;
  renderTrigger?: (actions: {
    hasValue: boolean;
    openCropEditor: () => void;
    openFilePicker: () => void;
  }) => ReactNode;
};

type CropState = {
  zoom: number;
  offsetX: number;
  offsetY: number;
};

const DEFAULT_CROP: CropState = {
  zoom: 1.15,
  offsetX: 0,
  offsetY: 0,
};
const DEFAULT_MAX_FILE_BYTES = 2 * 1024 * 1024;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function estimateDataUrlBytes(value: string) {
  const base64 = String(value || "").split(",")[1] || "";
  return Math.ceil((base64.length * 3) / 4);
}

export function calculateCropSourceRect(
  imageWidth: number,
  imageHeight: number,
  aspectRatio: number,
  crop: CropState,
) {
  const safeImageWidth = Math.max(1, Number(imageWidth) || 1);
  const safeImageHeight = Math.max(1, Number(imageHeight) || 1);
  const safeAspectRatio = Math.max(0.01, Number(aspectRatio) || 1);
  const zoom = Math.max(1, Number(crop.zoom) || 1);
  const sourceAspect = safeImageWidth / safeImageHeight;
  let croppedWidth = safeImageWidth / zoom;
  let croppedHeight = croppedWidth / safeAspectRatio;

  if (sourceAspect > safeAspectRatio) {
    croppedHeight = safeImageHeight / zoom;
    croppedWidth = croppedHeight * safeAspectRatio;
  }

  croppedWidth = Math.min(safeImageWidth, croppedWidth);
  croppedHeight = Math.min(safeImageHeight, croppedHeight);

  const maxOffsetX = Math.max(0, (safeImageWidth - croppedWidth) / 2);
  const maxOffsetY = Math.max(0, (safeImageHeight - croppedHeight) / 2);
  const translateX = (clamp(crop.offsetX, -100, 100) / 100) * maxOffsetX;
  const translateY = (clamp(crop.offsetY, -100, 100) / 100) * maxOffsetY;

  return {
    sourceX: clamp(
      (safeImageWidth - croppedWidth) / 2 + translateX,
      0,
      safeImageWidth - croppedWidth,
    ),
    sourceY: clamp(
      (safeImageHeight - croppedHeight) / 2 + translateY,
      0,
      safeImageHeight - croppedHeight,
    ),
    croppedWidth,
    croppedHeight,
  };
}

function renderCroppedImage(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  aspectRatio: number,
  crop: CropState,
  outputWidth = 1200
) {
  const context = canvas.getContext("2d");
  if (!context) return;

  const outputHeight = Math.round(outputWidth / aspectRatio);
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const { sourceX, sourceY, croppedWidth, croppedHeight } =
    calculateCropSourceRect(image.width, image.height, aspectRatio, crop);

  context.clearRect(0, 0, outputWidth, outputHeight);
  context.drawImage(
    image,
    sourceX,
    sourceY,
    croppedWidth,
    croppedHeight,
    0,
    0,
    outputWidth,
    outputHeight
  );
}

export default function ImageUploadField({
  label,
  value,
  onChange,
  onRemove,
  aspectRatio = 1,
  previewSquare = false,
  previewCircle = false,
  outputWidth = 1200,
  maxFileBytes = DEFAULT_MAX_FILE_BYTES,
  applyCropLabel,
  renderTrigger,
}: ImageUploadFieldProps) {
  const [pendingImage, setPendingImage] = useState("");
  const [crop, setCrop] = useState<CropState>(DEFAULT_CROP);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [cropError, setCropError] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [applyingCrop, setApplyingCrop] = useState(false);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const outputCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useBodyScrollLock(cropModalOpen);

  const imageElement = useMemo(() => {
    if (!pendingImage) return null;
    const image = new Image();
    if (!pendingImage.startsWith("data:")) {
      image.crossOrigin = "anonymous";
    }
    image.src = pendingImage;
    return image;
  }, [pendingImage]);

  useEffect(() => {
    if (!imageElement || !previewCanvasRef.current || !cropModalOpen) return;

    const handleLoad = () => {
      setPreviewLoaded(true);
      renderCroppedImage(
        imageElement,
        previewCanvasRef.current!,
        aspectRatio,
        crop,
        460
      );
    };

    imageElement.addEventListener("load", handleLoad);
    if (imageElement.complete) {
      handleLoad();
    }
    return () => imageElement.removeEventListener("load", handleLoad);
  }, [imageElement, aspectRatio, crop, cropModalOpen]);

  useEffect(() => {
    if (
      !previewLoaded ||
      !imageElement ||
      !previewCanvasRef.current ||
      !cropModalOpen
    ) {
      return;
    }
    renderCroppedImage(imageElement, previewCanvasRef.current, aspectRatio, crop, 460);
  }, [previewLoaded, imageElement, aspectRatio, crop, cropModalOpen]);

  const openCropModal = (source: string) => {
    if (!source) return;
    setPendingImage(source);
    setCrop(DEFAULT_CROP);
    setPreviewLoaded(false);
    setCropError("");
    setCropModalOpen(true);
  };

  const closeCropModal = () => {
    setCropModalOpen(false);
    setCropError("");
    setPendingImage("");
    setPreviewLoaded(false);
    setIsDraggingCrop(false);
  };

  const onPickFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      openCropModal(String(reader.result || ""));
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const applyCrop = async () => {
    if (!imageElement || !outputCanvasRef.current) return;
    try {
      setApplyingCrop(true);
      const canvas = outputCanvasRef.current;
      let nextImage = "";
      let nextWidth = Math.max(320, Math.round(outputWidth));
      let quality = 0.9;
      for (let attempt = 0; attempt < 10; attempt += 1) {
        renderCroppedImage(imageElement, canvas, aspectRatio, crop, nextWidth);
        nextImage = canvas.toDataURL("image/jpeg", quality);
        if (estimateDataUrlBytes(nextImage) <= maxFileBytes) break;
        if (quality > 0.72) {
          quality = Number((quality - 0.08).toFixed(2));
        } else {
          nextWidth = Math.max(320, Math.round(nextWidth * 0.82));
        }
      }
      if (estimateDataUrlBytes(nextImage) > maxFileBytes) {
        setCropError(siteTexts.imageUpload.cropError);
        return;
      }
      await onChange(nextImage);
      closeCropModal();
    } catch (err) {
      setCropError(
        err instanceof Error ? err.message : siteTexts.imageUpload.cropError,
      );
    } finally {
      setApplyingCrop(false);
    }
  };

  const onWheelCrop = (event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.08 : -0.08;
    setCrop((prev) => ({
      ...prev,
      zoom: clamp(Number((prev.zoom + delta).toFixed(2)), 1, 3),
    }));
  };

  const onCropPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY };
    setIsDraggingCrop(true);
  };

  const onCropPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDraggingCrop) return;
    const deltaX = event.clientX - dragRef.current.x;
    const deltaY = event.clientY - dragRef.current.y;
    dragRef.current = { x: event.clientX, y: event.clientY };

    setCrop((prev) => ({
      ...prev,
      offsetX: clamp(prev.offsetX + deltaX * 0.9, -100, 100),
      offsetY: clamp(prev.offsetY + deltaY * 0.9, -100, 100),
    }));
  };

  const onCropPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDraggingCrop(false);
  };

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      onChange={onPickFile}
      style={{ display: "none" }}
    />
  );

  const cropModal = cropModalOpen ? (
    <div
      className="image-cropper-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`${siteTexts.imageUpload.cropImageOf} ${label}`}
      onClick={closeCropModal}
    >
      <div
        className="image-cropper-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="image-cropper-modal-header">
          <h4>{siteTexts.imageUpload.cropTitle}</h4>
        </div>

        <div className="image-cropper">
          <div
            className={`image-cropper-stage${isDraggingCrop ? " is-dragging" : ""}${
              previewCircle ? " is-circle-preview" : ""
            }`}
            onWheel={onWheelCrop}
            onPointerDown={onCropPointerDown}
            onPointerMove={onCropPointerMove}
            onPointerUp={onCropPointerUp}
            onPointerCancel={onCropPointerUp}
          >
            <canvas ref={previewCanvasRef} className="image-cropper-preview" />
            {previewCircle && <div className="image-cropper-circle-mask" />}
            <div className="image-cropper-grid" />
          </div>

          <div className="image-cropper-controls">
            <label className="field image-cropper-field">
              <span>{siteTexts.imageUpload.zoom}</span>
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={crop.zoom}
                onChange={(event) =>
                  setCrop((prev) => ({ ...prev, zoom: Number(event.target.value) }))
                }
              />
            </label>

            <label className="field image-cropper-field">
              <span>{siteTexts.imageUpload.horizontalAdjust}</span>
              <input
                type="range"
                min="-100"
                max="100"
                step="1"
                value={crop.offsetX}
                onChange={(event) =>
                  setCrop((prev) => ({ ...prev, offsetX: Number(event.target.value) }))
                }
              />
            </label>

            <label className="field image-cropper-field">
              <span>{siteTexts.imageUpload.verticalAdjust}</span>
              <input
                type="range"
                min="-100"
                max="100"
                step="1"
                value={crop.offsetY}
                onChange={(event) =>
                  setCrop((prev) => ({ ...prev, offsetY: Number(event.target.value) }))
                }
              />
            </label>
          </div>

          {cropError && <p className="form-error">{cropError}</p>}

          <div className="image-cropper-actions">
            <Button type="button" variant="pill" onClick={closeCropModal}>
              {siteTexts.common.cancel}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={applyCrop}
              disabled={applyingCrop}
            >
              {applyCropLabel || siteTexts.imageUpload.applyCrop}
            </Button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  if (renderTrigger) {
    return (
      <>
        {renderTrigger({
          hasValue: Boolean(value),
          openCropEditor: () => (value ? openCropModal(value) : fileInputRef.current?.click()),
          openFilePicker: () => fileInputRef.current?.click(),
        })}
        {fileInput}
        {cropModal}
        <canvas ref={outputCanvasRef} style={{ display: "none" }} />
      </>
    );
  }

  return (
    <div className="field image-field">
      <span>{label}</span>
      <div className="image-field-actions">
        <Button
          type="button"
          variant="pill"
          onClick={() => fileInputRef.current?.click()}
        >
          {siteTexts.imageUpload.change}
        </Button>
        <Button
          type="button"
          variant="pill"
          onClick={() => openCropModal(value)}
          disabled={!value}
        >
          {siteTexts.imageUpload.edit}
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={() => {
            if (onRemove) {
              void onRemove();
              return;
            }
            onChange("");
          }}
          disabled={!value}
        >
          {siteTexts.imageUpload.remove}
        </Button>
      </div>

      <button
        type="button"
        className={`image-field-preview-wrap image-field-preview-trigger${
          previewSquare ? " is-square-preview" : ""
        }${previewCircle ? " is-circle-preview" : ""}`}
        onClick={() => fileInputRef.current?.click()}
        aria-label={`${siteTexts.imageUpload.selectImageFor} ${label}`}
      >
        {value ? (
          <img
            className="image-field-preview"
            src={value}
            alt={`${label} ${siteTexts.imageUpload.previewSuffix}`}
          />
        ) : (
          <div className="image-field-empty">{siteTexts.imageUpload.noImageSelected}</div>
        )}
      </button>

      <p className="image-field-help">
        {siteTexts.imageUpload.helpText}
      </p>

      {fileInput}
      {cropModal}

      <canvas ref={outputCanvasRef} style={{ display: "none" }} />
    </div>
  );
}
